/**
 * String manipulation utilities
 */

/**
 * Capitalize the first character of a string.
 * e.g., "sent" -> "Sent"
 */
export const capitalize = (value: string): string => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * Convert kebab-case to PascalCase for CSS module class names
 * e.g., "pending-data-enrichment" -> "PendingDataEnrichment"
 */
export const kebabToPascalCase = (str: string): string => {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

/**
 * Convert kebab-case to camelCase for CSS module class names
 * e.g., "urgency-critical" -> "urgencyCritical"
 */
export const kebabToCamelCase = (str: string): string => {
  const parts = str.split('-');
  return (
    parts[0] +
    parts
      .slice(1)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('')
  );
};

/**
 * Format a person's display name, preferring `preferred_name` when set and non-blank.
 * Falls back to `first_name` otherwise. Mirrors the backend `formatFullName` helper.
 *
 * @example
 * formatPersonName({ preferred_name: 'Alex', first_name: 'Alexander', last_name: 'Smith' })
 * // => "Alex Smith"
 *
 * formatPersonName({ preferred_name: null, first_name: 'Jane', last_name: 'Doe' })
 * // => "Jane Doe"
 */
export const formatPersonName = (p: {
  preferred_name?: string | null;
  first_name: string;
  last_name: string;
}): string => {
  return `${formatGivenName(p)} ${p.last_name}`.trim();
};

/**
 * Format the given (first) name alone, preferring `preferred_name` when set and non-blank.
 * Use for personalized greetings where the surname would be too formal.
 *
 * @example
 * formatGivenName({ preferred_name: 'Ivan', first_name: 'Andrii' }) // => "Ivan"
 * formatGivenName({ preferred_name: null, first_name: 'Andrii' })   // => "Andrii"
 */
export const formatGivenName = (p: { preferred_name?: string | null; first_name: string }): string => {
  return p.preferred_name?.trim() || p.first_name;
};

/**
 * Format a person as "<PreferredOrFirst> <Last> (Active|Inactive)" — the lead-picker display
 * convention (#936 R9). A blank `preferred_name` falls back to `first_name` with no placeholder.
 * Matches the server's `user_name` for assigned leads, so candidates and saved leads read the same.
 *
 * @example
 * formatUserWithStatus({ preferred_name: 'Alex', first_name: 'Alexander', last_name: 'Smith', is_active: true })
 * // => "Alex Smith (Active)"
 */
export const formatUserWithStatus = (p: {
  preferred_name?: string | null;
  first_name: string;
  last_name: string;
  is_active: boolean;
}): string => {
  return `${formatPersonName(p)} (${p.is_active ? 'Active' : 'Inactive'})`;
};

/**
 * True when the string parses as an absolute http(s) URL.
 *
 * Used to gate author-supplied link targets: anything else (javascript:,
 * data:, relative paths) is rejected so the author is told rather than having
 * their input silently rewritten.
 *
 * @example
 * isValidHttpUrl('https://example.com') // => true
 * isValidHttpUrl('javascript:alert(1)') // => false
 */
export const isValidHttpUrl = (candidate: string): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return false;
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:';
};
